
import { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscriptionData } from "@/hooks/useSubscriptionData";
import { signUp as authSignUp, signIn as authSignIn, signInWithGoogle as authSignInWithGoogle, signOut as authSignOut } from "@/utils/authUtils";
import { AuthContextType } from "@/types/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  const { subscriptionData, setSubscriptionData, refreshSubscription } = useSubscriptionData();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Sempre definir isLoading como false após mudanças de estado
        setIsLoading(false);
        
        // Buscar subscription de forma assíncrona, sem bloquear a UI
        if (session?.user) {
          // Não aguardar a subscription para não travar a UI
          refreshSubscription(session).catch(error => {
            console.warn("⚠️ Subscription refresh failed, continuing without it:", error);
          });
        } else {
          setSubscriptionData({
            subscribed: false,
            subscription_tier: null,
            subscription_end: null,
            subscription_id: null,
            webhook_secret: null,
            is_owner: false,
          });
        }
      }
    );

    // Initial session check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Sempre definir isLoading como false
      setIsLoading(false);
      
      if (session?.user) {
        // Não aguardar a subscription para não travar a inicialização
        refreshSubscription(session).catch(error => {
          console.warn("⚠️ Initial subscription refresh failed, continuing without it:", error);
        });
      }
    }).catch(error => {
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      await authSignUp(email, password);
      
      toast({
        title: "Cadastro realizado com sucesso",
        description: "Verifique seu email para confirmar seu cadastro.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao criar conta",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      await authSignIn(email, password);

      toast({
        title: "Login realizado com sucesso",
        description: "Bem-vindo de volta!",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao fazer login",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      await authSignInWithGoogle();
    } catch (error: any) {
      toast({
        title: "Erro ao fazer login com Google",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      
      await authSignOut();
      
      toast({
        title: "Logout realizado com sucesso",
        description: "Até logo!",
      });
    } catch (error: any) {
      // Mesmo com erro, mostrar mensagem de sucesso para não confundir o usuário
      // pois o authSignOut já faz a limpeza forçada em caso de erro
      toast({
        title: "Sessão encerrada",
        description: "Você foi desconectado com sucesso.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    session,
    isLoading,
    subscriptionData,
    refreshSubscription: () => refreshSubscription(session),
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
